import React, { useEffect, useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Sparkles } from 'lucide-react';

export const ShuffleAnimation = ({ 
  isActive, 
  onComplete,
  className 
}) => {
  const [phase, setPhase] = useState(0);
  const [shuffleCount, setShuffleCount] = useState(0);

  // Create deck of cards for animation
  const cards = useMemo(() => 
    Array.from({ length: 12 }, (_, i) => ({
      id: i,
      initialX: 0,
      initialY: i * -2,
    })), []
  );

  // Pre-computed animation values for each card
  const animationValues = useMemo(() => [
    { splitX: -80, splitY: -20, rotation: -15, riffleDelay: 0 },
    { splitX: -75, splitY: -18, rotation: -12, riffleDelay: 0.03 },
    { splitX: -70, splitY: -15, rotation: -10, riffleDelay: 0.06 },
    { splitX: -65, splitY: -12, rotation: -8, riffleDelay: 0.09 },
    { splitX: -60, splitY: -8, rotation: -5, riffleDelay: 0.12 },
    { splitX: -55, splitY: -5, rotation: -3, riffleDelay: 0.15 },
    { splitX: 55, splitY: -5, rotation: 3, riffleDelay: 0.18 },
    { splitX: 60, splitY: -8, rotation: 5, riffleDelay: 0.21 },
    { splitX: 65, splitY: -12, rotation: 8, riffleDelay: 0.24 },
    { splitX: 70, splitY: -15, rotation: 10, riffleDelay: 0.27 },
    { splitX: 75, splitY: -18, rotation: 12, riffleDelay: 0.30 },
    { splitX: 80, splitY: -20, rotation: 15, riffleDelay: 0.33 },
  ], []);

  useEffect(() => {
    if (isActive) {
      setPhase(1);
      setShuffleCount(0);

      // Animation sequence
      const timeline = [
        { phase: 2, delay: 300 },   // Split deck
        { phase: 3, delay: 700 },   // Riffle 1
        { phase: 4, delay: 1200 },  // Merge 1
        { phase: 5, delay: 1600 },  // Split again
        { phase: 6, delay: 2000 },  // Riffle 2
        { phase: 7, delay: 2500 },  // Merge 2
        { phase: 8, delay: 2900 },  // Fan out
        { phase: 9, delay: 3400 },  // Collect
        { phase: 10, delay: 3800 }, // Final stack
      ];

      const timers = timeline.map(({ phase: p, delay }) => 
        setTimeout(() => {
          setPhase(p);
          if (p === 3 || p === 6) setShuffleCount(prev => prev + 1);
        }, delay)
      );

      // Complete
      const completeTimer = setTimeout(() => {
        setPhase(0);
        onComplete?.();
      }, 4200);

      return () => {
        timers.forEach(t => clearTimeout(t));
        clearTimeout(completeTimer);
      };
    }
  }, [isActive, onComplete]);

  const getCardStyle = (index) => {
    const anim = animationValues[index] || animationValues[0];
    const isLeftHalf = index < 6;
    const stackOffset = index * -2;
    
    const baseStyle = {
      transition: 'all 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)',
    };

    switch (phase) {
      case 1:
        // Initial stack
        return {
          ...baseStyle,
          transform: `translateY(${stackOffset}px) rotate(0deg)`,
          opacity: 1,
        };
      
      case 2:
      case 5:
        // Split deck into two halves
        return {
          ...baseStyle,
          transform: `
            translateX(${anim.splitX}px) 
            translateY(${anim.splitY + stackOffset}px) 
            rotate(${anim.rotation}deg)
          `,
          opacity: 1,
        };
      
      case 3:
      case 6:
        // Riffle shuffle - cards interleave
        const riffleY = -100 + (index * 15);
        const riffleX = isLeftHalf ? -20 + (index * 8) : 20 - ((index - 6) * 8);
        return {
          ...baseStyle,
          transition: `all 0.25s ease ${anim.riffleDelay}s`,
          transform: `
            translateX(${riffleX}px) 
            translateY(${riffleY}px) 
            rotate(${(index % 2 === 0 ? -1 : 1) * 5}deg)
          `,
          opacity: 1,
        };
      
      case 4:
      case 7:
        // Merge back together
        const mergeOffset = (index - 6) * -3;
        return {
          ...baseStyle,
          transform: `translateY(${mergeOffset}px) rotate(0deg)`,
          opacity: 1,
        };
      
      case 8:
        // Fan out dramatically
        const fanAngle = (index - 5.5) * 12;
        const fanY = Math.abs(index - 5.5) * 8;
        return {
          ...baseStyle,
          transform: `
            translateY(${fanY}px) 
            rotate(${fanAngle}deg)
          `,
          transformOrigin: 'bottom center',
          opacity: 1,
        };
      
      case 9:
        // Collect back
        const collectX = (index - 5.5) * 5;
        return {
          ...baseStyle,
          transform: `translateX(${collectX}px) translateY(0px) rotate(0deg)`,
          opacity: 1,
        };
      
      case 10:
        // Final neat stack
        return {
          ...baseStyle,
          transform: `translateY(${index * -2}px) rotate(0deg)`,
          opacity: 1,
        };
      
      default:
        return {
          ...baseStyle,
          transform: 'translateY(0) rotate(0deg)',
          opacity: 0,
        };
    }
  };

  if (!isActive && phase === 0) return null;

  return (
    <div className={cn(
      "fixed inset-0 z-50 flex items-center justify-center",
      "bg-[#0a1628]/95 backdrop-blur-md",
      className
    )}>
      {/* Animated background circles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[500px] h-[500px] rounded-full border border-[#e89b47]/20"
          style={{
            animation: 'spin 8s linear infinite',
          }}
        />
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full border border-[#d4c4a8]/15"
          style={{
            animation: 'spin 12s linear infinite reverse',
          }}
        />
        <div 
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] h-[300px] rounded-full border border-[#e89b47]/10"
          style={{
            animation: 'spin 6s linear infinite',
          }}
        />
      </div>

      {/* Floating particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(20)].map((_, i) => (
          <div
            key={`particle-${i}`}
            className="absolute w-1 h-1 bg-[#e89b47]/40 rounded-full"
            style={{
              left: `${10 + (i * 4.5)}%`,
              top: `${20 + (i % 5) * 15}%`,
              animation: `float ${2 + (i % 3)}s ease-in-out infinite`,
              animationDelay: `${i * 0.1}s`,
            }}
          />
        ))}
      </div>
      
      {/* Main card shuffling area */}
      <div 
        className="relative"
        style={{ 
          width: '200px', 
          height: '280px',
          perspective: '1000px',
        }}
      >
        {/* Glow effect under cards */}
        <div 
          className={cn(
            "absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2",
            "w-48 h-48 rounded-full transition-all duration-500",
            (phase >= 3 && phase <= 6) ? "bg-[#e89b47]/30 blur-3xl scale-150" : "bg-[#e89b47]/10 blur-2xl scale-100"
          )}
        />

        {/* The shuffling cards */}
        {cards.map((card, index) => (
          <div
            key={card.id}
            className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-32 h-48 rounded-xl overflow-hidden"
            style={getCardStyle(index)}
          >
            {/* Card design */}
            <div className="w-full h-full bg-gradient-to-br from-[#1e2a4a] to-[#141c33] border-4 border-[#d4c4a8] rounded-xl">
              {/* Inner border */}
              <div className="absolute inset-2 border border-[#d4c4a8]/30 rounded-lg"/>
              
              {/* Center symbol */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="relative">
                  <div className="w-12 h-12 rounded-full border border-[#e89b47]/40 flex items-center justify-center">
                    <span className="text-[#e89b47] text-xl">✦</span>
                  </div>
                </div>
              </div>

              {/* Corner decorations */}
              <div className="absolute top-2 left-2 w-2 h-2 border-t border-l border-[#d4c4a8]/40"/>
              <div className="absolute top-2 right-2 w-2 h-2 border-t border-r border-[#d4c4a8]/40"/>
              <div className="absolute bottom-2 left-2 w-2 h-2 border-b border-l border-[#d4c4a8]/40"/>
              <div className="absolute bottom-2 right-2 w-2 h-2 border-b border-r border-[#d4c4a8]/40"/>
            </div>
          </div>
        ))}
      </div>

      {/* Shuffling text and progress */}
      <div className="absolute bottom-1/4 text-center">
        <div className="flex items-center justify-center gap-2 mb-3">
          <Sparkles className={cn(
            "w-5 h-5 text-[#e89b47] transition-transform duration-300",
            (phase >= 3 && phase <= 6) && "scale-125"
          )}/>
          <p className="font-display text-[#e8dcc8] text-xl">
            {phase >= 8 ? 'Almost ready...' : 'Shuffling the deck'}
          </p>
          <Sparkles className={cn(
            "w-5 h-5 text-[#e89b47] transition-transform duration-300",
            (phase >= 3 && phase <= 6) && "scale-125"
          )}/>
        </div>
        
        {/* Shuffle progress indicator */}
        <div className="flex justify-center gap-2 mb-3">
          {[1, 2].map((num) => (
            <div 
              key={num}
              className={cn(
                "w-3 h-3 rounded-full border-2 transition-all duration-300",
                shuffleCount >= num 
                  ? "bg-[#e89b47] border-[#e89b47]" 
                  : "border-[#d4c4a8]/40"
              )}
            />
          ))}
        </div>

        {/* Animated dots */}
        <div className="flex justify-center gap-1">
          {[0, 1, 2].map((i) => (
            <div 
              key={i}
              className="w-2 h-2 rounded-full bg-[#e89b47] animate-pulse"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
      </div>

      {/* CSS for animations */}
      <style>{`
        @keyframes spin {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to { transform: translate(-50%, -50%) rotate(360deg); }
        }
        @keyframes float {
          0%, 100% { transform: translateY(0px) scale(1); opacity: 0.4; }
          50% { transform: translateY(-20px) scale(1.5); opacity: 0.8; }
        }
      `}</style>
    </div>
  );
};

export default ShuffleAnimation;